<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="table-content-title" :field="fields?.title" tag="h2" />
      <div
        class="table-content-table"
        v-if="tableHead.length || tableBody.length"
        :style="tableStyle"
      >
        <!-- 表头 -->
        <div class="table-content-head" v-if="tableHead.length">
          <div v-for="(head, idx) in tableHead" :key="idx" class="table-content-head-cell">
            <JssRichText :field="head.fields?.text" />
          </div>
        </div>

        <!-- 表体 -->
        <div class="table-content-body">
          <div v-for="(row, rowIdx) in tableBody" :key="rowIdx" class="table-content-row">
            <div
              v-for="(cell, colIdx) in row.fields?.list || []"
              :key="colIdx"
              class="table-content-row-cell"
            >
              <div
                v-for="(el, elIdx) in cell.fields?.list || []"
                :key="elIdx"
                class="table-content-text"
                :class="{ 'is-clickable': el.fields?.openModal?.value }"
                @click="onTextClick(el)"
              >
                <JssRichText :field="el.fields?.text" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContentGrid>

    <!-- 弹窗 -->
    <transition name="table-content-fade">
      <div v-if="modalVisible" class="table-content-modal-mask" @click.self="closeModal">
        <div class="table-content-modal">
          <span class="table-content-modal-close" @click="closeModal">&times;</span>
          <div class="table-content-modal-body">
            <JssRichText :field="modalContent" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import utils from '@/utils';

export default {
  name: 'TableContent',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'table-content'));
    const rootStyle = computed(() => utils.overall.baseComStyle(props.fields));

    const tableHead = computed(() => props.fields?.tableHead || []);
    const tableBody = computed(() => props.fields?.tableBody || []);

    // 列数取表头数量
    const tableStyle = computed(() => ({
      '--cols': tableHead.value.length || 1,
    }));

    // 弹窗状态
    const modalVisible = ref(false);
    const modalContent = ref(null);

    const onTextClick = (el) => {
      console.log('el', el);
      if (!el.fields?.openModal?.value) return;
      modalContent.value = el.fields?.body;
      modalVisible.value = true;
    };

    const closeModal = () => {
      modalVisible.value = false;
      modalContent.value = null;
    };

    return {
      rootClass,
      rootStyle,
      tableHead,
      tableBody,
      tableStyle,
      modalVisible,
      modalContent,
      onTextClick,
      closeModal,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.table-content {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-table {
    width: 100%;
    overflow-x: auto;
    margin-top: 24px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &-head {
    display: grid;
    grid-template-columns: repeat(var(--cols, 1), 200px);
    min-width: max-content;
    background: $blue-night;
    color: $white;

    &-cell {
      padding: 16px 12px;
      text-align: center;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.4;
      word-break: break-word;
    }
  }

  &-body {
    display: flex;
    flex-direction: column;
  }

  &-row {
    display: grid;
    grid-template-columns: repeat(var(--cols, 1), 200px);
    min-width: max-content;
    border-bottom: 1px solid #e5e7eb;
    background: $white;

    &:nth-child(even) {
      background: #f9fafb;
    }

    &-cell {
      padding: 16px 12px;
      font-size: 14px;
      line-height: 1.5;
      color: #364153;
      word-break: break-word;
      border-right: 1px solid #e5e7eb;

      &:last-child {
        border-right: none;
      }
    }
  }

  &-text {
    & + & {
      margin-top: 8px;
    }

    &.is-clickable {
      cursor: pointer;
      color: $blue-night;
      text-decoration: underline;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  // 弹窗
  &-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 80vw;
    max-height: 60vh;
    background: $white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &-mask {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    }

    &-close {
      position: absolute;
      top: 12px;
      right: 16px;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
      color: #999;
      z-index: 1;

      &:hover {
        color: #333;
      }
    }

    &-body {
      padding: 32px 24px 24px;
      overflow-y: auto;
      font-size: 16px;
      line-height: 1.6;
      color: #364153;
    }
  }

  @include tablet {
    &-head {
      grid-template-columns: repeat(var(--cols, 1), 1fr);
      min-width: 0;

      &-cell {
        padding: 20px 16px;
        font-size: 18px;
      }
    }

    &-row {
      grid-template-columns: repeat(var(--cols, 1), 1fr);
      min-width: 0;

      &-cell {
        padding: 20px 16px;
        font-size: 16px;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;
  }
}

// 弹窗过渡动画
.table-content-fade {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.3s;

    .table-content-modal {
      transition: transform 0.3s;
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .table-content-modal {
      transform: translate(-50%, -50%) scale(0.9);
    }
  }
}
</style>
